import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Severity, usePushNotifications } from 'services/NotificationService';
import { getCreativeImagesFromParams } from '../utils/getCreativeImagesFromParams';
import { useCreativeTemplatesBuilderStore } from 'stores/CreativeTemplatesBuilderStore';
import {
  ImageTypeEnum,
  useArticleGroupAiImagesLazyQuery,
  useArticleGroupImagesLazyQuery,
  useCreativeImagesLazyQuery,
  useCreativeTemplateEditorLazyQuery,
  useCreativeVideos
} from 'api';

const IMAGES_PER_PAGE = 50;

export const useApi = () => {
  const { id: articleGroupId } = useParams<{ id: string }>();

  const notify = usePushNotifications();
  const store = useCreativeTemplatesBuilderStore();

  const [creativeTemplateEditorQuery, { data: articleGroupData, loading: articleGroupLoading }] =
    useCreativeTemplateEditorLazyQuery({ id: articleGroupId });

  const [articleGroupAiImageLazyQuery, { data: articleGroupAiImagesData, loading: aiImagesLoading }] =
    useArticleGroupAiImagesLazyQuery({ id: articleGroupId });

  const [articleGroupImagesLazyQuery, { data: articleGroupImagesData, loading: articleGroupImagesLoading }] =
    useArticleGroupImagesLazyQuery({
      id: articleGroupId
    });

  const [creativeImagesLazyQuery, { data: creativeImagesData, loading: creativeImagesLoading }] =
    useCreativeImagesLazyQuery({
      articleGroupId,
      imagesPerPage: IMAGES_PER_PAGE,
      creativeImageIds: getCreativeImagesFromParams()
    });

  const [creativeVideosLazyQuery, { data: creativeVideosData, loading: videosLoading }] = useCreativeVideos({
    articleGroupId
  });

  useEffect(() => {
    if (!store.videos.length) {
      creativeVideosLazyQuery();
    }

    if (!store.images.length) {
      store.setIsImagesLoading(true);

      creativeImagesLazyQuery();
      articleGroupImagesLazyQuery();
      articleGroupAiImageLazyQuery();
    }

    if (!store.articleGroup) {
      creativeTemplateEditorQuery();
    }
  }, []);

  useEffect(() => {
    if (!articleGroupData) return;

    if (store.articleGroup) {
      articleGroupData.articleGroup.descriptions = store.articleGroup.descriptions;
      store.setArticleGroup(articleGroupData.articleGroup);
    } else {
      store.setArticleGroup(articleGroupData.articleGroup);
    }
  }, [articleGroupData]);

  useEffect(() => {
    if (!creativeVideosData) return;

    if (creativeVideosData.creativeVideos.errors) {
      notify({
        title: 'Failed to fetch videos',
        message: creativeVideosData.creativeVideos.errors,
        severity: Severity.ERROR
      });
    }

    store.setVideos(creativeVideosData.creativeVideos.videos ?? []);
  }, [creativeVideosData]);

  useEffect(() => {
    if (!creativeImagesData) return;

    const creativeImages =
      creativeImagesData.creativeImages.edges?.map((edge) => ({
        id: edge!.node!.id!,
        url: edge!.node!.url!,
        type: ImageTypeEnum.CreativeImage,
        imageType: edge!.node!.imageType!,
        ctr: edge!.node!.metrics?.ctr || 0,
        spent: edge!.node!.metrics?.spent || 0
      })) || [];

    store.setImages([...store.images, ...creativeImages]);
  }, [creativeImagesData]);

  useEffect(() => {
    if (!articleGroupImagesData) return;

    const articleGroupImages =
      articleGroupImagesData.articleGroup.ogImages.map((image) => ({
        id: Number(image.id),
        url: image.url,
        type: ImageTypeEnum.ArticleImage,
        imageType: null,
        ctr: image.metrics?.ctr || 0,
        spent: image.metrics?.spent || 0
      })) || [];

    store.setImages([...store.images, ...articleGroupImages]);
  }, [articleGroupImagesData]);

  useEffect(() => {
    if (!articleGroupAiImagesData) return;

    const articleGroupAiImages =
      articleGroupAiImagesData.articleGroup.aiImages.map((image) => ({
        id: image.id,
        url: image.url,
        type: ImageTypeEnum.AiImage,
        imageType: null,
        ctr: image.metrics?.ctr || 0,
        spent: image.metrics?.spent || 0
      })) || [];

    store.setImages([...store.images, ...articleGroupAiImages]);
  }, [articleGroupAiImagesData]);

  useEffect(() => {
    if (creativeImagesData && articleGroupImagesData && articleGroupAiImagesData) {
      store.setIsImagesLoading(false);
    }
  }, [creativeImagesData, articleGroupImagesData, articleGroupAiImagesData]);

  return {
    loading:
      articleGroupLoading || aiImagesLoading || articleGroupImagesLoading || creativeImagesLoading || videosLoading
  };
};
